import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Grid, Card, CardContent, CardHeader, Typography, Button,
    LinearProgress, Tab, Tabs, Alert, Paper, Snackbar, IconButton,
    FormControlLabel, Switch, Tooltip, useTheme, useMediaQuery
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    CloudDownload, CloudUpload,
    Close as CloseIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import {
    LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid,
    Tooltip as RechartsTooltip, Legend, ResponsiveContainer, Label
} from 'recharts';
import { get } from '../services/apiService';
import { useClientAuth } from '../contexts/ClientAuthContext';
import { useTrafficSocket } from '../hooks/trafficSocketHooks';

function ClientTraffic() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { client } = useClientAuth();
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
    const useWebSocket = true; // Constante en lugar de estado ya que no cambia
    const [useMixedUnits, setUseMixedUnits] = useState(true); // Controla si usamos unidades mixtas

    // Estados para datos obtenidos por REST API (como respaldo)
    const [dailyData, setDailyData] = useState([]);
    const [monthlyData, setMonthlyData] = useState([]);

    // Usar el hook para WebSocket
    const {
        realtimeData,
        isConnected,
        lastUpdated,
        formatRate,
        extractRateValue,
        chartData: originalChartData,
        refreshData
    } = useTrafficSocket();

    // Formatear valor para mostrar con unidades correctas
    const formatValueWithUnit = useCallback((valueKbps) => {
        // Si el valor es grande, mostrar en Mbps
        if (valueKbps >= 1024) {
            return `${(valueKbps / 1024).toFixed(2)} Mbps`;
        }
        // Si es pequeño, mostrar en Kbps
        return `${valueKbps.toFixed(2)} Kbps`;
    }, []);

    // Procesar datos del gráfico para mantener consistencia con los paneles superiores
    const chartData = useMemo(() => {
        if (!originalChartData || originalChartData.length === 0) return [];
        if (!realtimeData) return originalChartData;

        return originalChartData.map(item => {
            // Asegurar que tenemos los valores en Kbps para cálculos
            const downloadRateKbps = item.downloadRateKbps ||
                extractRateValue(item.downloadRateOriginal || item.downloadRate);
            const uploadRateKbps = item.uploadRateKbps ||
                extractRateValue(item.uploadRateOriginal || item.uploadRate);

            // Para el gráfico, siempre usamos valores en Kbps para la escala correcta
            return {
                ...item,
                downloadRate: downloadRateKbps,
                uploadRate: uploadRateKbps,
                // Determinar unidades para tooltips y pie del gráfico
                downloadUnit: downloadRateKbps >= 1024 ? 'Mbps' : 'Kbps',
                uploadUnit: uploadRateKbps >= 1024 ? 'Mbps' : 'Kbps',
                // Formatear valores para mostrar coherentemente
                downloadFormatted: formatValueWithUnit(downloadRateKbps),
                uploadFormatted: formatValueWithUnit(uploadRateKbps)
            };
        });
    }, [originalChartData, realtimeData, extractRateValue, formatValueWithUnit]);

    // Cambiar de pestaña
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Función para cargar datos diarios
    const fetchDailyData = useCallback(async () => {
        if (!client) return;

        try {
            setLoading(true);
            const response = await get('/api/client/traffic/daily?days=7', true);

            if (response && Array.isArray(response)) {
                setDailyData(response.map(item => ({
                    day: item.date,
                    downloadGB: Math.round(parseFloat(item.download || 0) * 100) / 100,
                    uploadGB: Math.round(parseFloat(item.upload || 0) * 100) / 100
                })));
            }

            setLoading(false);
        } catch (error) {
            console.error('Error al cargar datos diarios:', error);
            setError('No se pudieron cargar los datos diarios. Por favor, inténtelo de nuevo.');
            setLoading(false);
        }
    }, [client]);

    // Función para cargar datos mensuales
    const fetchMonthlyData = useCallback(async () => {
        if (!client) return;

        try {
            setLoading(true);
            const response = await get('/api/client/traffic/monthly?months=12', true);

            if (response && Array.isArray(response)) {
                setMonthlyData(response.map(item => ({
                    month: item.month,
                    downloadGB: Math.round(parseFloat(item.download || 0) * 100) / 100,
                    uploadGB: Math.round(parseFloat(item.upload || 0) * 100) / 100
                })));
            }

            setLoading(false);
        } catch (error) {
            console.error('Error al cargar datos mensuales:', error);
            setError('No se pudieron cargar los datos mensuales. Por favor, inténtelo de nuevo.');
            setLoading(false);
        }
    }, [client]);

    // Cargar datos según la pestaña activa
    useEffect(() => {
        if (!client) return;

        // Cargar datos según la pestaña
        if (activeTab === 1) {
            fetchDailyData();
        } else if (activeTab === 2) {
            fetchMonthlyData();
        }
    }, [activeTab, client, fetchDailyData, fetchMonthlyData]);

    // Manejar actualización manual
    const handleRefresh = () => {
        if (!client) {
            setError('No hay sesión de cliente activa. Por favor, inicie sesión nuevamente.');
            return;
        }

        if (activeTab === 0) {
            refreshData();
        } else if (activeTab === 1) {
            fetchDailyData();
        } else if (activeTab === 2) {
            fetchMonthlyData();
        }
    };

    // Convertir valores del panel de velocidad actual para mostrar de manera coherente
    const displayValues = useMemo(() => {
        if (!realtimeData) return { download: '0 Kbps', upload: '0 Kbps' };

        return {
            download: formatRate(realtimeData.downloadRate),
            upload: formatRate(realtimeData.uploadRate),
        };
    }, [realtimeData, formatRate]);

    // Cerrar notificación
    const handleCloseNotification = () => {
        setNotification(prev => ({ ...prev, open: false }));
    };

    // Función para formatear valores en el tooltip del gráfico con unidades correctas
    const formatTooltipValue = (value, name, props) => {
        if (value === undefined || value === null)
            return ['0 Kbps', name === 'downloadRate' ? 'Descarga' : 'Subida'];

        // Obtener el punto de datos actual
        const dataPoint = props.payload[0];
        if (!dataPoint)
            return [formatValueWithUnit(value), name === 'downloadRate' ? 'Descarga' : 'Subida'];

        // Determinar si estamos procesando download o upload
        const isDownload = name === 'downloadRate';

        // Usar texto formateado previamente
        const displayText = isDownload ? dataPoint.downloadFormatted : dataPoint.uploadFormatted;

        return [
            displayText,
            isDownload ? 'Descarga' : 'Subida'
        ];
    };

    // Toggle para cambiar entre unidades mixtas y fijas
    const handleToggleUnits = () => {
        setUseMixedUnits(!useMixedUnits);
    };

    // Si no hay cliente autenticado, mostrar mensaje
    if (!client) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning">
                    No hay una sesión activa. Por favor, inicie sesión nuevamente para ver los datos de tráfico.
                </Alert>
            </Box>
        );
    }

    // Obtener los valores actuales para el pie del gráfico
    const getCurrentValues = () => {
        if (!realtimeData) return { download: '0 Kbps', upload: '0 Kbps' };

        // Formatear valores con las unidades correctas
        return {
            download: formatValueWithUnit(realtimeData.downloadRateKbps),
            upload: formatValueWithUnit(realtimeData.uploadRateKbps)
        };
    };

    // Valores actuales formateados
    const currentValues = getCurrentValues();

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                    Visualiza el consumo de tu plan de internet en tiempo real, historial semanal y mensual.
                </Typography>
            </Box>

            {/* Mostrar estado de conexión */}
            {activeTab === 0 && (
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color={isConnected ? 'success.main' : 'text.secondary'}>
                        {isConnected
                            ? '✓ Conectado en tiempo real'
                            : '⚠ Usando actualizaciones periódicas'}
                    </Typography>

                    {!isConnected && useWebSocket && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={refreshData}
                            startIcon={<RefreshIcon />}
                        >
                            Reconectar
                        </Button>
                    )}
                </Box>
            )}

            {/* Mensajes de error */}
            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 3 }}
                    onClose={() => setError(null)}
                    action={
                        <Button color="inherit" size="small" onClick={handleRefresh}>
                            Reintentar
                        </Button>
                    }
                >
                    {error}
                </Alert>
            )}

            {/* Tabs de navegación */}
            <Paper sx={{ mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <Tab label="TIEMPO REAL" />
                    <Tab label="ÚLTIMOS 7 DÍAS" />
                    <Tab label="HISTORIAL MENSUAL" />
                </Tabs>
            </Paper>

            {loading && <LinearProgress sx={{ mb: 2 }} />}

            {/* Tab 0: Tiempo Real */}
            {activeTab === 0 && (
                <Box>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, gap: 2, mb: 2 }}>
                        <Typography variant="h6">
                            Consumo en Tiempo Real
                            {lastUpdated && (
                                <Typography variant="body2" component="span" sx={{ ml: { xs: 0, sm: 2 }, mt: { xs: 0.5, sm: 0 }, display: { xs: 'block', sm: 'inline-block' }, color: 'text.secondary' }}>
                                    Actualizado: {lastUpdated.toLocaleTimeString()}
                                </Typography>
                            )}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'space-between', md: 'flex-end' }, width: { xs: '100%', md: 'auto' }, gap: 2 }}>
                            <Tooltip title="Cambiar entre unidades mixtas (Kbps/Mbps) o solo Mbps">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={useMixedUnits}
                                            onChange={handleToggleUnits}
                                            size="small"
                                        />
                                    }
                                    label={<Typography variant="body2">Unidades mixtas</Typography>}
                                    sx={{ mr: 0 }}
                                />
                            </Tooltip>
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={handleRefresh}
                                disabled={loading}
                            >
                                Actualizar
                            </Button>
                        </Box>
                    </Box>

                    <Grid container spacing={3}>
                        {/* Panel de descarga */}
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                    <CloudDownload color="primary" sx={{ fontSize: 48, mb: 2 }} />
                                    <Typography variant="h6">Descarga (Download)</Typography>
                                    <Typography variant="h3" sx={{ my: 2 }}>
                                        {displayValues.download}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Velocidad de descarga actual
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Panel de subida */}
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                    <CloudUpload color="success" sx={{ fontSize: 48, mb: 2 }} />
                                    <Typography variant="h6">Subida (Upload)</Typography>
                                    <Typography variant="h3" sx={{ my: 2 }}>
                                        {displayValues.upload}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Velocidad de subida actual
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Gráfico de velocidad */}
                        {chartData.length > 0 && (
                            <Grid item xs={12}>
                                <Card>
                                    <CardHeader
                                        title="Velocidad en Tiempo Real"
                                        titleTypographyProps={{ fontWeight: 'medium' }}
                                        action={
                                            <Tooltip title="Los valores se muestran en Kbps o Mbps según la magnitud">
                                                <InfoIcon color="primary" />
                                            </Tooltip>
                                        }
                                    />
                                    <CardContent>
                                        <ResponsiveContainer width="100%" height={isMobile ? 240 : 350}>
                                            <LineChart
                                                data={chartData}
                                                margin={{ top: 5, right: isMobile ? 5 : 30, left: isMobile ? -25 : 20, bottom: 5 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                <XAxis
                                                    dataKey="time"
                                                    tick={{ fontSize: 10 }}
                                                    tickMargin={8}
                                                />
                                                {/* YAxis con formateo adaptativo Kbps/Mbps */}
                                                <YAxis
                                                    yAxisId="left"
                                                    tick={{ fontSize: 10 }}
                                                    tickFormatter={(value) => {
                                                        if (value >= 1024) {
                                                            return `${(value / 1024).toFixed(0)}M`;
                                                        } else {
                                                            return `${value.toFixed(0)}K`;
                                                        }
                                                    }}
                                                >
                                                    {!isMobile && (
                                                        <Label
                                                            value="Velocidad (Kbps/Mbps)"
                                                            angle={-90}
                                                            position="insideLeft"
                                                            style={{ textAnchor: 'middle', fontSize: 12 }}
                                                        />
                                                    )}
                                                </YAxis>
                                                <RechartsTooltip
                                                    formatter={formatTooltipValue}
                                                    labelFormatter={(label) => `Hora: ${label}`}
                                                    contentStyle={{
                                                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                                    }}
                                                />
                                                <Legend
                                                    formatter={(value) => value === 'downloadRate' ? 'Descarga' : 'Subida'}
                                                />
                                                <Line
                                                    yAxisId="left"
                                                    type="monotone"
                                                    dataKey="downloadRate"
                                                    name="downloadRate"
                                                    stroke="#3f51b5"
                                                    strokeWidth={2}
                                                    dot={false}
                                                    activeDot={{ r: 6 }}
                                                    isAnimationActive={true}
                                                    animationDuration={300}
                                                />
                                                <Line
                                                    yAxisId="left"
                                                    type="monotone"
                                                    dataKey="uploadRate"
                                                    name="uploadRate"
                                                    stroke="#4caf50"
                                                    strokeWidth={2}
                                                    dot={false}
                                                    activeDot={{ r: 6 }}
                                                    isAnimationActive={true}
                                                    animationDuration={300}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>

                                        {/* Mostrar valores actuales con las mismas unidades que el panel superior */}
                                        <Box sx={{ mt: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: { xs: 'center', sm: 'flex-end' }, alignItems: 'center', gap: { xs: 1, sm: 3 } }}>
                                            <Typography variant="body2">
                                                Hora: {lastUpdated ? lastUpdated.toLocaleTimeString() : ''}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#3f51b5', fontWeight: 600 }}>
                                                Descarga: {currentValues.download}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 600 }}>
                                                Subida: {currentValues.upload}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            )}

            {/* Tab 1: Últimos 7 días */}
            {activeTab === 1 && (
                <Box>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, mb: 2 }}>
                        <Typography variant="h6">
                            Tráfico Diario (Últimos 7 días)
                            {lastUpdated && (
                                <Typography variant="body2" component="span" sx={{ ml: { xs: 0, sm: 2 }, mt: { xs: 0.5, sm: 0 }, display: { xs: 'block', sm: 'inline-block' }, color: 'text.secondary' }}>
                                    Actualizado: {lastUpdated.toLocaleTimeString()}
                                </Typography>
                            )}
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={handleRefresh}
                            disabled={loading}
                        >
                            Actualizar
                        </Button>
                    </Box>

                    <Grid container spacing={3}>
                        {/* Gráfico de consumo diario */}
                        <Grid item xs={12}>
                            <Card>
                                <CardHeader
                                    title="Consumo Diario (GB)"
                                    titleTypographyProps={{ fontWeight: 'medium' }}
                                />
                                <CardContent>
                                    {dailyData.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={isMobile ? 260 : 400}>
                                            <AreaChart
                                                data={dailyData}
                                                margin={{ top: 10, right: isMobile ? 5 : 30, left: isMobile ? -25 : 0, bottom: 0 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                <XAxis
                                                    dataKey="day"
                                                    tick={{ fontSize: 12 }}
                                                    tickMargin={8}
                                                />
                                                <YAxis
                                                    tick={{ fontSize: 12 }}
                                                    tickMargin={8}
                                                    label={{
                                                        value: 'GB',
                                                        angle: -90,
                                                        position: 'insideLeft',
                                                        fontSize: 12
                                                    }}
                                                />
                                                <RechartsTooltip
                                                    formatter={(value) => [`${value} GB`, '']}
                                                    contentStyle={{
                                                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                                    }}
                                                    labelStyle={{ color: '#333', fontWeight: 'bold' }}
                                                />
                                                <Legend />
                                                <Area
                                                    type="monotone"
                                                    dataKey="downloadGB"
                                                    name="Download"
                                                    stackId="1"
                                                    stroke="#3f51b5"
                                                    fill="#3f51b5"
                                                    fillOpacity={0.6}
                                                    isAnimationActive={true}
                                                    animationDuration={500}
                                                />
                                                <Area
                                                    type="monotone"
                                                    dataKey="uploadGB"
                                                    name="Upload"
                                                    stackId="1"
                                                    stroke="#4caf50"
                                                    fill="#4caf50"
                                                    fillOpacity={0.6}
                                                    isAnimationActive={true}
                                                    animationDuration={500}
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <Box sx={{ p: 3, textAlign: 'center' }}>
                                            <Typography variant="body1" color="text.secondary">
                                                No hay datos disponibles para este período
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            )}

            {/* Tab 2: Historial Mensual */}
            {activeTab === 2 && (
                <Box>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, mb: 2 }}>
                        <Typography variant="h6">
                            Historial Mensual
                            {lastUpdated && (
                                <Typography variant="body2" component="span" sx={{ ml: { xs: 0, sm: 2 }, mt: { xs: 0.5, sm: 0 }, display: { xs: 'block', sm: 'inline-block' }, color: 'text.secondary' }}>
                                    Actualizado: {lastUpdated.toLocaleTimeString()}
                                </Typography>
                            )}
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={handleRefresh}
                            disabled={loading}
                        >
                            Actualizar
                        </Button>
                    </Box>

                    <Grid container spacing={3}>
                        {/* Gráfico de consumo mensual */}
                        <Grid item xs={12}>
                            <Card>
                                <CardHeader
                                    title="Consumo Mensual (GB)"
                                    titleTypographyProps={{ fontWeight: 'medium' }}
                                />
                                <CardContent>
                                    {monthlyData.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={isMobile ? 260 : 400}>
                                            <BarChart
                                                data={monthlyData}
                                                margin={{ top: 20, right: isMobile ? 5 : 30, left: isMobile ? -25 : 20, bottom: 5 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                <XAxis
                                                    dataKey="month"
                                                    tick={{ fontSize: 12 }}
                                                    tickMargin={8}
                                                />
                                                <YAxis
                                                    tick={{ fontSize: 12 }}
                                                    tickMargin={8}
                                                    label={{
                                                        value: 'GB',
                                                        angle: -90,
                                                        position: 'insideLeft',
                                                        fontSize: 12
                                                    }}
                                                />
                                                <RechartsTooltip
                                                    formatter={(value) => [`${value} GB`, '']}
                                                    contentStyle={{
                                                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                                    }}
                                                    labelStyle={{ color: '#333', fontWeight: 'bold' }}
                                                />
                                                <Legend />
                                                <Bar
                                                    dataKey="downloadGB"
                                                    name="Download"
                                                    stackId="a"
                                                    fill="#3f51b5"
                                                    radius={[4, 4, 0, 0]}
                                                    isAnimationActive={true}
                                                    animationDuration={500}
                                                />
                                                <Bar
                                                    dataKey="uploadGB"
                                                    name="Upload"
                                                    stackId="a"
                                                    fill="#4caf50"
                                                    radius={[4, 4, 0, 0]}
                                                    isAnimationActive={true}
                                                    animationDuration={500}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <Box sx={{ p: 3, textAlign: 'center' }}>
                                            <Typography variant="body1" color="text.secondary">
                                                No hay datos disponibles para este período
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            )}

            {/* Notificación Toast */}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleCloseNotification}
                message={notification.message}
                action={
                    <IconButton
                        size="small"
                        color="inherit"
                        onClick={handleCloseNotification}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                }
            />
        </Box>
    );
}

export default ClientTraffic;